import { useEffect, useState } from "react";
import { CRISIS_RESOURCES } from "../lib/crisis";

/**
 * Support card.
 *
 * The most important tonal element in the product. It can fire on a false
 * positive, so it must never look like an error: no red, no alert icon, no
 * takeover, nothing that punishes honest journaling. A soft sage card, one
 * slowly breathing dot on the same baseline as the headline, real help as
 * large easy targets, and — when the parent allows it — a graceful way to put
 * it away.
 *
 * ── Why nothing here touches focus ────────────────────────────────────────
 * This card is rendered directly underneath the journal textarea and appears
 * *while the person is still typing*. Moving focus to it would pull the caret
 * out of the textarea mid-sentence and dismiss the iOS keyboard — taking the
 * keyboard away from someone in the middle of a disclosure. That is the worst
 * thing this product could do, so focus is never moved from here.
 *
 * Announcement is handled instead by a live region that is mounted empty and
 * filled a beat later, so assistive tech reliably reads the change (a region
 * inserted in the same tick as its content is usually not announced). The
 * caret never leaves the textarea, and the lifelines are the very next stop in
 * the tab order for anyone who wants to reach them.
 */
export default function CrisisSupport({ onDismiss, variant = "crisis" }) {
  const isGentle = variant === "sustained";
  const title = isGentle ? "It's been a heavy stretch." : CRISIS_RESOURCES.headline;

  // Mount the live region empty, then write into it. Announced without ever
  // touching the caret.
  const [announcement, setAnnouncement] = useState("");
  useEffect(() => {
    const t = setTimeout(
      () => setAnnouncement(`${title} Support options are available just below your note.`),
      150
    );
    return () => clearTimeout(t);
  }, [title]);

  return (
    <section aria-label="Support" className="shell-support">
      <p className="sr-only" aria-live="polite">{announcement}</p>

      <div className="shell-support-head">
        <span className="shell-support-mark" aria-hidden="true" />
        <h2 className="shell-support-title">{title}</h2>
      </div>

      <p className="shell-support-body">
        {isGentle
          ? "You've had a run of low days. Movement helps, and it isn't the whole answer — talking to someone can make a real difference."
          : CRISIS_RESOURCES.body}
      </p>

      <div className="shell-support-lines">
        {CRISIS_RESOURCES.lines.map((l) => (
          <a key={l.label} href={l.href} className="shell-support-line">
            <span>
              <span className="shell-support-line-label">{l.label}</span>
              <span className="shell-support-line-detail">{l.detail}</span>
            </span>
            <span className="shell-support-line-go" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 5 16 12 9 19" />
              </svg>
            </span>
          </a>
        ))}
      </div>

      <p className="shell-support-foot">{CRISIS_RESOURCES.footer}</p>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="btn btn-ghost btn-full shell-support-dismiss"
        >
          Not right now
        </button>
      )}
    </section>
  );
}
